import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { CFR_CATEGORIES } from "../../constants/cfrCategories";
import "./BrowseCauses.css";

function normalizeCategories(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function categoryName(cat) {
  return cat.label || cat.name || cat.title || cat.value;
}

function categoryDescription(cat) {
  return cat.description || cat.summary || "Explore local organizations and needs connected to this community focus area.";
}

export default function BrowseCauses() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        const [orgSnap, needSnap] = await Promise.all([
          getDocs(collection(db, "organizations")),
          getDocs(collection(db, "needs")),
        ]);

       console.log("CAUSE DEBUG org docs:", orgSnap.docs.length);
console.log("CAUSE DEBUG need docs:", needSnap.docs.length);

const orgRows = orgSnap.docs.map(d => ({ id: d.id, ...d.data() }));
const needRows = needSnap.docs.map(d => ({ id: d.id, ...d.data() }));

console.log("CAUSE DEBUG sample orgs:", orgRows.slice(0, 5).map(o => ({
  id: o.id,
  name: o.name || o.orgName || o.organizationName,
  cfrCategories: o.cfrCategories
})));

console.log("CAUSE DEBUG sample needs:", needRows.slice(0, 5).map(n => ({
  id: n.id,
  itemName: n.itemName || n.title || n.name,
  status: n.status,
  cfrCategories: n.cfrCategories
})));

setOrganizations(orgRows);
setNeeds(needRows);



      } catch (err) {
        console.error("BrowseCauses load error", err);
      } finally {
        setLoading(false);
      }
      
    }

    loadData();
  }, []);

  const causeCards = useMemo(() => {
    return CFR_CATEGORIES.map(cat => {
      const catId = cat.value;

      const matchingOrgs = organizations.filter(org =>
        normalizeCategories(org.cfrCategories).includes(catId)
      );

      const matchingNeeds = needs.filter(need =>
        normalizeCategories(need.cfrCategories).includes(catId)
      );

      const openNeeds = matchingNeeds.filter(need =>
        ["open", "available", "active"].includes((need.status || "open").toLowerCase())
      );

      const fundedNeeds = matchingNeeds.filter(need =>
        ["funded", "fulfilled", "completed", "sponsored"].includes((need.status || "").toLowerCase())
      );

      return {
        ...cat,
        orgCount: matchingOrgs.length,
        needCount: matchingNeeds.length,
        openNeedCount: openNeeds.length,
        fundedNeedCount: fundedNeeds.length,
      };
    });
  }, [organizations, needs]);

  const filteredCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return causeCards;

    return causeCards.filter(cat =>
      categoryName(cat).toLowerCase().includes(q) ||
      categoryDescription(cat).toLowerCase().includes(q) ||
      cat.value.toLowerCase().includes(q)
    );
  }, [causeCards, search]);

  return (
    <div className="causes-page">
      <header className="causes-header">
        <div>
          <button className="causes-back" onClick={() => navigate("/give/dashboard")}>
            ← Dashboard
          </button>
          <h1>Find a cause you care about</h1>
          <p>
            Browse local needs and organizations by Community Focus Report area.
          </p>
        </div>

        <button className="causes-secondary" onClick={() => navigate("/give/orgs")}>
          Browse organizations
        </button>
      </header>

      <section className="causes-hero">
        <div>
          <div className="causes-kicker">Community Focus Areas</div>
          <h2>Start with the issue, then find the people doing the work.</h2>
          <p>
            SomebodyGives connects donor support to specific local needs, organizations,
            and community priorities.
          </p>
        </div>
      </section>

      <div className="causes-search-row">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search causes, like food, housing, children, education..."
          className="causes-search"
        />
      </div>

      {loading ? (
        <div className="causes-loading">Loading causes...</div>
      ) : (
        <div className="causes-grid">
          {filteredCards.map(cat => (
            <article
              key={cat.value}
              className="cause-card"
              onClick={() => navigate(`/give/causes/${cat.value}`)}
            >
              <div className="cause-card-top">
                <div className="cause-icon">{cat.icon || "◎"}</div>
                <div>
                  <h3>{categoryName(cat)}</h3>
                  <p>{categoryDescription(cat)}</p>
                  <div className="cause-card-examples">
  <div className="cause-card-examples-label">
    Example needs
  </div>

  {cat.exampleNeeds?.slice(0, 3).map(example => (
    <div key={example} className="cause-example-item">
      • {example}
    </div>
  ))}
</div>
                </div>
              </div>

              <div className="cause-stats">
                <div>
                  <strong>{cat.orgCount}</strong>
                  <span>organization{cat.orgCount === 1 ? "" : "s"}</span>
                </div>
                <div>
                  <strong>{cat.openNeedCount}</strong>
                  <span>open need{cat.openNeedCount === 1 ? "" : "s"}</span>
                </div>
                <div>
                  <strong>{cat.fundedNeedCount}</strong>
                  <span>funded</span>
                </div>
              </div>

              <button className="cause-card-action">
                Explore {categoryName(cat)} →
              </button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}